import { useEffect, useState, type ReactNode } from "react";
import {
  ArrowRightOnRectangleIcon,
  ChatBubbleBottomLeftTextIcon,
  LayoutGridIcon,
  QuestionMarkCircleIcon,
  TwoChevronsLeftIcon,
  TwoChevronsRightIcon,
} from "@sortera/react-icons";
import { chooseColorScheme, currentColorScheme, onColorScheme } from "../../integrations/color-scheme.ts";
import { BrandMark } from "../../ui/brand-mark/index.ts";
import { MoonIcon, SunIcon } from "../../ui/scheme-icons/index.ts";
import { WritingGuideDialog } from "../writing-guide/index.ts";
import styles from "./rail.module.css";

export type RailView = "chat" | "projects";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "")).toUpperCase() || "?";
}

/**
 * An icon with its name beside it on hover and focus — the rail has no room for words, and a
 * browser title is too slow to explain anything. The design system has no tooltip yet.
 */
function RailButton({
  label,
  active,
  className,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  className?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className={`${styles.item} ${className ?? ""}`} data-active={active} aria-label={label} onClick={onClick}>
      {children}
      <span className={styles.tip} aria-hidden>
        {label}
      </span>
    </button>
  );
}

/** The narrow column of destinations, always visible on wide screens. */
export function Rail(props: {
  view: RailView;
  userName: string;
  onChat: () => void;
  onProjects: () => void;
  /** The mark at the top: back to the start page, whatever state the rest is in. */
  onHome: () => void;
  onSignOut: () => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}) {
  const [guideOpen, setGuideOpen] = useState(false);
  const [scheme, setScheme] = useState(currentColorScheme);
  useEffect(() => onColorScheme(setScheme), []);

  return (
    <nav className={styles.rail} aria-label="Huvudmeny">
      <RailButton label="Till startsidan" className={styles.home} onClick={props.onHome}>
        <BrandMark showName={false} />
      </RailButton>

      <div className={styles.group}>
        <RailButton label="Chatt med Claude" active={props.view === "chat"} onClick={props.onChat}>
          <ChatBubbleBottomLeftTextIcon size={20} />
        </RailButton>
        <RailButton label="Alla projekt" active={props.view === "projects"} onClick={props.onProjects}>
          <LayoutGridIcon size={20} />
        </RailButton>
        <RailButton label="Så beskriver du vad du vill ha" onClick={() => setGuideOpen(true)}>
          <QuestionMarkCircleIcon size={20} />
        </RailButton>
      </div>

      <div className={styles.bottom}>
        <RailButton
          label={props.sidebarOpen ? "Dölj sidomenyn" : "Visa sidomenyn"}
          className={styles.wideOnly}
          onClick={props.onToggleSidebar}
        >
          {props.sidebarOpen ? <TwoChevronsLeftIcon size={20} /> : <TwoChevronsRightIcon size={20} />}
        </RailButton>
        {/* Shows where a press takes you, as the sidebar button does. */}
        <RailButton
          label={scheme === "dark" ? "Ljust läge" : "Mörkt läge"}
          onClick={() => chooseColorScheme(scheme === "dark" ? "light" : "dark")}
        >
          {scheme === "dark" ? <SunIcon size={20} /> : <MoonIcon size={20} />}
        </RailButton>
        <RailButton label="Logga ut" onClick={props.onSignOut}>
          <ArrowRightOnRectangleIcon size={20} />
        </RailButton>
        <span className={styles.avatar} title={props.userName}>
          {initials(props.userName)}
        </span>
      </div>

      <WritingGuideDialog open={guideOpen} onOpenChange={setGuideOpen} />
    </nav>
  );
}
